import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { trackPhoneCall, trackQuoteRequest } from "@/utils/gtag";

const hashMenu = [
  { label: "HOME", to: "#top", path: "/" },
  { label: "회사소개", to: "#about", path: "/#about" },
  { label: "사업분야", to: "#services", path: "/#services" },
  { label: "블로그", to: "#blog", path: "/#blog" },
];

const pageMenu = [
  { label: "시공사례", path: "/portfolio" },
  { label: "문의하기", path: "/contact" },
];

const LOGO_URL =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/b541ae47-1bf4-433d-8cc6-b0741de80222_compressed_-1.webp";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("#top");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section based on scroll position (home only)
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveHash("");
      return;
    }
    const ids = hashMenu.map((m) => m.to.replace("#", "")).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          const id = visible[0].target.id;
          if (id === "top" && window.scrollY > 200) return;
          setActiveHash("#" + id);
        }
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const topSentinel = document.getElementById("top");
    if (topSentinel) observer.observe(topSentinel);

    return () => observer.disconnect();
  }, [location.pathname]);

  // Handle hash-based smooth scroll on home page
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
      e.preventDefault();
      if (hash === "#top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveHash("#top");
        return;
      }
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        setActiveHash(hash);
      }
    },
    [],
  );

  // Navigate to hash on home page (from other pages)
  const handleHashNav = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
      e.preventDefault();
      if (location.pathname === "/") {
        const hash = path.split("#")[1] || "top";
        if (hash === "top") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        navigate(path);
      }
    },
    [location.pathname, navigate],
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  // Determine which menu item is active based on path
  const currentPath = location.pathname;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-white/95 backdrop-blur border-b border-neutral-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src={LOGO_URL}
              alt="JI&C 제이아이앤씨"
              className="h-10 w-auto rounded-md"
            />
            <div className="leading-tight">
              <div
                className={`font-hero text-lg tracking-tight ${
                  solid ? "text-navy-900" : "text-white"
                }`}
              >
                JI&amp;C
              </div>
              <div
                className={`text-[11px] ${
                  solid ? "text-neutral-500" : "text-white/70"
                }`}
              >
                제이아이앤씨
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {hashMenu.map((m) => {
              const isActive = activeHash === m.to;
              return (
                <a
                  key={m.to}
                  href={m.path}
                  onClick={(e) => handleHashNav(e, m.path)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    solid
                      ? isActive
                        ? "text-point-600"
                        : "text-navy-900 hover:text-point-600"
                      : isActive
                      ? "text-point-300"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {m.label}
                </a>
              );
            })}
            {pageMenu.map((m) => {
              const isActive = currentPath === m.path;
              return (
                <Link
                  key={m.path}
                  to={m.path}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    solid
                      ? isActive
                        ? "text-point-600"
                        : "text-navy-900 hover:text-point-600"
                      : isActive
                      ? "text-point-300"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {m.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:0517270477"
              onClick={() => trackPhoneCall()}
              className={`flex items-center gap-2 text-sm font-medium cursor-pointer whitespace-nowrap ${
                solid ? "text-navy-900" : "text-white"
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-phone-line text-lg"></i>
              </div>
              051-727-0477
            </a>
            <Link
              to="/contact"
              onClick={() => trackQuoteRequest()}
              className="px-5 py-2.5 bg-point-500 hover:bg-point-600 text-white text-sm font-semibold rounded-full transition-all duration-500 ease-out hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
            >
              무료 견적문의
            </Link>
          </div>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle Menu"
          >
            <i
              className={`text-2xl ${
                solid ? "text-navy-900" : "text-white"
              } ${open ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-200">
          <div className="px-6 py-4 flex flex-col gap-1">
            {hashMenu.map((m) => {
              const isActive = activeHash === m.to;
              return (
                <a
                  key={m.to}
                  href={m.path}
                  onClick={(e) => {
                    handleHashNav(e, m.path);
                    setOpen(false);
                  }}
                  className={`px-3 py-3 text-base font-medium rounded-md cursor-pointer ${
                    isActive ? "text-point-600 bg-point-50" : "text-navy-900"
                  }`}
                >
                  {m.label}
                </a>
              );
            })}
            {pageMenu.map((m) => {
              const isActive = currentPath === m.path;
              return (
                <Link
                  key={m.path}
                  to={m.path}
                  onClick={() => setOpen(false)}
                  className={`px-3 py-3 text-base font-medium rounded-md cursor-pointer ${
                    isActive ? "text-point-600 bg-point-50" : "text-navy-900"
                  }`}
                >
                  {m.label}
                </Link>
              );
            })}
            <Link
              to="/contact"
              onClick={() => {
                setOpen(false);
                trackQuoteRequest();
              }}
              className="mt-2 px-5 py-3 bg-point-500 text-white text-center text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap"
            >
              무료 견적문의
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}